import { getItem, setItem } from '@/utils/storage';

class FeeCalculator {
  constructor() {
    this.fundPercent = {
      NGN: 0.014,
      HABARI: 0.005,
      USD: 0.038,
      THEPEER: 0.005,
      FLUIDCOINS: 0.01,
      APPLEPAY: 1
    };

    this.currencyConfig = {
      'USD': {
        rate: this.fundPercent.USD, 
        cap: 3,      // $3 cap
        bankTransferRate: this.fundPercent.HABARI
      },
      'NGN': {
        rate: this.fundPercent.NGN,
        cap: 2000,   // ₦2000 cap
        bankTransferRate: this.fundPercent.HABARI 
      },
      'EUR': {
        rate: this.fundPercent.USD, 
        cap: 15,     // €15 cap
        bankTransferRate: this.fundPercent.HABARI
      }
    };

    this.feeRules = {
      'Fund with Fintech Apps': {
        calculate: (amount, currency) => {
          const config = this.getCurrencyConfig(currency);
          const fixedFee = this.getFixedFeeForCurrency(currency);
          const fee = this.fundPercent.THEPEER * amount + fixedFee;
          return Math.min(fee, config.cap);
        }
      },
      'Fund with Fluidcoins': {
        calculate: (amount, currency) => {
          const config = this.getCurrencyConfig(currency);
          const fee = (amount / 100) * this.fundPercent.FLUIDCOINS * 100;
          return Math.min(fee, config.cap);
        }
      },
      'Fund with bank transfer': {
        calculate: (amount, currency) => {
          const config = this.getCurrencyConfig(currency);
          const rate = config.bankTransferRate;
          const fee = rate * amount;
          return Math.min(fee, config.cap);
        }
      },
      'P2P Transfers': {
        calculate: (amount, currency) => {
          const config = this.getCurrencyConfig(currency);
          const fee = config.rate * amount;
          return Math.min(fee, config.cap);
        }
      },
      'Apple Pay': {
        calculate: (amount, currency) => {
          const config = this.getCurrencyConfig(currency);
          const fee = this.fundPercent.APPLEPAY * amount; 
          return Math.min(fee, config.cap);
        }
      }, 
      'Crypto': {
        calculate: (amount, currency) => {
          const fee = 0.01 * amount; 
          return fee;
        }
      }
    };
  }

  getCurrencyConfig(currency) {
    return this.currencyConfig[currency] || this.currencyConfig['USD'];
  }

  getFixedFeeForCurrency(currency) {
    const fixedFees = {
      'USD': 0.30,
      'NGN': 25,
      'EUR': 0.25
    };
    return fixedFees[currency] || fixedFees['USD'];
  }

  calculateFee(fundOption, amount, currency = 'USD') {
    const rule = this.feeRules[fundOption];
    if (!rule) {
      const config = this.getCurrencyConfig(currency);
      const fee = config.rate * amount;
      return Math.min(fee, config.cap);
    }
    
    return rule.calculate(amount, currency);
  }

  getFeeBreakdown(fundOption, amount, currency = 'USD') {
    const config = this.getCurrencyConfig(currency);
    const calculatedFee = this.calculateFee(fundOption, amount, currency);
    const uncappedFee = config.rate * amount;
    
    return {
      rate: config.rate,
      ratePercentage: (config.rate * 100).toFixed(2) + '%',
      cap: config.cap,
      uncappedFee: uncappedFee,
      actualFee: calculatedFee,
      isCapped: uncappedFee > config.cap,
      currency: currency
    };
  }
}

export const fundingModule = {
  namespaced: true,
  
  state: {
    fundPayload: {
      amount: 0,
      tokens: 0,
      totalAmount: 0,
      currency: 'USD',
      transactionFee: 0
    },
    feeCalculator: new FeeCalculator()
  },

  getters: {
    transactionFee: (state) => (fundOptions, amount, currency) => {
      if (!amount || !fundOptions) return 0;
      return state.feeCalculator.calculateFee(fundOptions, amount, currency);
    },
    
    feeBreakdown: (state) => (fundOptions, amount, currency) => {
      if (!amount || !fundOptions) return null;
      return state.feeCalculator.getFeeBreakdown(fundOptions, amount, currency);
    },
    
    totalAmount: (state, getters) => (fundOptions, amount, currency) => {
      const fee = getters.transactionFee(fundOptions, amount, currency);
      return Math.round((amount + fee) * 100) / 100;
    },

    currentFundPayload: (state) => state.fundPayload
  },

  mutations: {
    SET_FUND_PAYLOAD(state, payload) {
      state.fundPayload = { ...state.fundPayload, ...payload };
    },

    SET_TRANSACTION_FEE(state, fee) {
      state.fundPayload.transactionFee = fee;
    },

    SET_TOTAL_AMOUNT(state, totalAmount) {
      state.fundPayload.totalAmount = totalAmount;
    },

    RESET_FUND_PAYLOAD(state) {
      state.fundPayload = {
        amount: 0,
        tokens: 0,
        totalAmount: 0,
        currency: 'USD',
        transactionFee: 0
      };
    }
  },

  actions: {
    async initializeFunding({ commit, dispatch }) {
      try {
        const storedPayload = getItem("fundPayload");
        if (storedPayload) {
          commit('SET_FUND_PAYLOAD', storedPayload);
        }
      } catch (error) {
        console.error('Error initializing funding:', error);
      }
    },

    async calculateFee({ commit, getters }, { fundOptions, amount, currency, tokens }) {
      try {
        if (!amount || !fundOptions) {
          throw new Error('Amount and fund options are required');
        }

        const fee = getters.transactionFee(fundOptions, amount, currency);
        const totalAmount = getters.totalAmount(fundOptions, amount, currency);
        
        const payload = {
          amount: Number(amount),
          tokens: Number(tokens?.toFixed(2) || 0),
          totalAmount,
          currency,
          transactionFee: fee
        };
        
        commit('SET_FUND_PAYLOAD', payload);
        
        setItem("fundPayload", payload);
        
        return payload;
      } catch (error) {
        console.error('Fee calculation error:', error);
        throw error;
      }
    },

    async updateFundingDetails({ dispatch }, payload) {
      return await dispatch('calculateFee', payload);
    },

    clearFunding({ commit }) {
      commit('RESET_FUND_PAYLOAD');
      try {
        localStorage.removeItem('fundPayload');
      } catch (error) {
        console.error('Error clearing funding data:', error);
      }
    }
  }
};